import { useEffect, useState } from "react";
import { Warehouse, Save, Loader2, Plus, Pencil, Trash2, X, Fuel, Gauge, Clock, MapPin } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const COST_FIELDS = [
  { k: "delivery_fuel_price", label: "Prix carburant (€/L)", icon: Fuel, step: "0.01" },
  { k: "delivery_consumption", label: "Consommation (L/100km)", icon: Gauge, step: "0.1" },
  { k: "delivery_cost_per_km", label: "Usure véhicule (€/km)", step: "0.01" },
  { k: "delivery_driver_cost_hour", label: "Coût chauffeur (€/h)", step: "1" },
  { k: "delivery_avg_speed", label: "Vitesse moyenne (km/h)", step: "1" },
  { k: "delivery_loading_time", label: "Temps de chargement (h)", icon: Clock, step: "0.1" },
  { k: "delivery_road_factor", label: "Coef. distance route", step: "0.05" },
  { k: "delivery_min_fee", label: "Forfait minimum (€)", step: "1" },
  { k: "shop_free_delivery_threshold", label: "Livraison offerte dès (€)", step: "10" },
];

const emptyDepot = { name: "", address: "", postal_code: "", city: "", lat: 0, lng: 0, active: true };

export default function Logistics() {
  const [s, setS] = useState(null);
  const [depots, setDepots] = useState([]);
  const [savingS, setSavingS] = useState(false);
  const [editingDepot, setEditingDepot] = useState(null);
  const [depotForm, setDepotForm] = useState(emptyDepot);
  const [savingD, setSavingD] = useState(false);

  const loadSettings = () => api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  const loadDepots = () => api.get("/depots").then((r) => setDepots(r.data)).catch(() => {});
  useEffect(() => { loadSettings(); loadDepots(); }, []);

  const saveSettings = async () => {
    setSavingS(true);
    const payload = { shop_delivery_enabled: s.shop_delivery_enabled,
      delivery_departments: (typeof s.delivery_departments === "string" ? s.delivery_departments.split(",") : s.delivery_departments).map((x) => String(x).trim()).filter(Boolean) };
    COST_FIELDS.forEach((f) => { payload[f.k] = Number(s[f.k] || 0); });
    try { await api.put("/settings", payload); toast.success("Paramètres enregistrés"); loadSettings(); }
    catch { toast.error("Erreur"); } finally { setSavingS(false); }
  };

  const startNewDepot = () => { setDepotForm(emptyDepot); setEditingDepot("new"); };
  const startEditDepot = (d) => { setDepotForm({ ...emptyDepot, ...d }); setEditingDepot(d.id); };
  const saveDepot = async () => {
    if (!depotForm.name.trim()) { toast.error("Nom requis"); return; }
    setSavingD(true);
    const payload = { name: depotForm.name, address: depotForm.address, postal_code: depotForm.postal_code, city: depotForm.city, lat: Number(depotForm.lat || 0), lng: Number(depotForm.lng || 0), active: depotForm.active !== false };
    try {
      if (editingDepot === "new") await api.post("/depots", payload);
      else await api.put(`/depots/${editingDepot}`, payload);
      toast.success("Dépôt enregistré"); setEditingDepot(null); loadDepots();
    } catch { toast.error("Erreur"); } finally { setSavingD(false); }
  };
  const removeDepot = async (id) => { if (!window.confirm("Supprimer ce dépôt ?")) return; await api.delete(`/depots/${id}`); toast.success("Supprimé"); loadDepots(); };

  const input = "w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";
  if (!s) return <p className="text-stone">Chargement…</p>;
  const depts = Array.isArray(s.delivery_departments) ? s.delivery_departments.join(", ") : s.delivery_departments;

  return (
    <div>
      <h1 className="font-display text-4xl text-forest mb-1 flex items-center gap-2"><Warehouse className="text-brand" /> Logistique</h1>
      <p className="text-stone mb-6">Dépôts, zone de livraison et coût de livraison dynamique</p>

      {/* Dépôts */}
      <div className="rounded-xl bg-white border border-border p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-forest text-lg">Dépôts</h2>
          <button onClick={startNewDepot} data-testid="depot-new-btn" className="rounded-full bg-brand text-cream px-4 py-2 text-sm font-semibold hover:bg-forest flex items-center gap-1.5"><Plus size={16} /> Ajouter</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {depots.map((d) => (
            <div key={d.id} className="rounded-lg border border-border p-4" data-testid={`depot-${d.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-forest flex items-center gap-1.5"><MapPin size={14} className="text-brand" /> {d.name}</p>
                  <p className="text-xs text-stone">{d.postal_code} {d.city}</p>
                  <p className="text-[11px] text-stone mt-1">{d.products_in_stock} produit(s) en stock · GPS {d.lat?.toFixed(3)}, {d.lng?.toFixed(3)}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEditDepot(d)} data-testid={`depot-edit-${d.id}`} className="text-stone hover:text-brand p-1.5"><Pencil size={15} /></button>
                  <button onClick={() => removeDepot(d.id)} data-testid={`depot-delete-${d.id}`} className="text-red-500 hover:bg-red-50 rounded p-1.5"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Paramètres de livraison */}
      <div className="rounded-xl bg-white border border-border p-6">
        <h2 className="font-semibold text-forest text-lg mb-4">Livraison & coûts</h2>
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input type="checkbox" checked={!!s.shop_delivery_enabled} onChange={(e) => setS({ ...s, shop_delivery_enabled: e.target.checked })} data-testid="delivery-enabled" className="h-4 w-4 accent-[#5a7d3c]" />
          <span className="text-sm text-forest font-medium">Livraison activée</span>
        </label>
        <div className="mb-4">
          <label className="text-xs text-stone">Départements livrés (séparés par des virgules)</label>
          <input className={input} value={depts} onChange={(e) => setS({ ...s, delivery_departments: e.target.value })} data-testid="delivery-departments" placeholder="13, 83, 84" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {COST_FIELDS.map((f) => (
            <div key={f.k}>
              <label className="text-xs text-stone flex items-center gap-1">{f.icon && <f.icon size={12} />} {f.label}</label>
              <input type="number" step={f.step} className={input} value={s[f.k] ?? 0} onChange={(e) => setS({ ...s, [f.k]: e.target.value })} data-testid={`cost-${f.k}`} />
            </div>
          ))}
        </div>
        <p className="text-xs text-stone mt-4 rounded-lg bg-[#F0EFEA] p-3">Coût livraison = <strong>Carburant</strong> (km A/R × conso × prix) + <strong>Véhicule</strong> (km A/R × €/km) + <strong>Chauffeur</strong> (durée × €/h). Distance estimée depuis le dépôt le plus proche ayant le stock.</p>
        <button onClick={saveSettings} disabled={savingS} data-testid="save-logistics" className="mt-4 rounded-full bg-brand text-cream px-6 py-3 font-semibold hover:bg-forest flex items-center gap-2 disabled:opacity-60">{savingS ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Enregistrer</button>
      </div>

      {editingDepot && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditingDepot(null)} data-testid="depot-modal">
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="font-semibold text-forest">{editingDepot === "new" ? "Nouveau dépôt" : "Modifier le dépôt"}</h3>
              <button onClick={() => setEditingDepot(null)} className="p-2 rounded-lg hover:bg-sage/50"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <input className={input} placeholder="Nom du dépôt *" value={depotForm.name} onChange={(e) => setDepotForm({ ...depotForm, name: e.target.value })} data-testid="depot-name" />
              <input className={input} placeholder="Adresse" value={depotForm.address} onChange={(e) => setDepotForm({ ...depotForm, address: e.target.value })} data-testid="depot-address" />
              <div className="grid grid-cols-2 gap-3">
                <input className={input} placeholder="Code postal" value={depotForm.postal_code} onChange={(e) => setDepotForm({ ...depotForm, postal_code: e.target.value })} data-testid="depot-postal" />
                <input className={input} placeholder="Ville" value={depotForm.city} onChange={(e) => setDepotForm({ ...depotForm, city: e.target.value })} data-testid="depot-city" />
              </div>
              <p className="text-xs text-stone">Les coordonnées GPS sont calculées automatiquement depuis l'adresse. Laissez à 0 pour un calcul auto.</p>
              <div className="grid grid-cols-2 gap-3">
                <input type="number" step="0.0001" className={input} placeholder="Latitude" value={depotForm.lat} onChange={(e) => setDepotForm({ ...depotForm, lat: e.target.value })} />
                <input type="number" step="0.0001" className={input} placeholder="Longitude" value={depotForm.lng} onChange={(e) => setDepotForm({ ...depotForm, lng: e.target.value })} />
              </div>
              <button onClick={saveDepot} disabled={savingD} data-testid="depot-save" className="w-full rounded-full bg-brand text-cream py-3 font-semibold hover:bg-forest flex items-center justify-center gap-2 disabled:opacity-60">{savingD ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
